import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Mail, Lock, User, School, UserPlus } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import Logo from '../common/Logo'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { TurnstileCaptcha, type TurnstileCaptchaHandle } from '../security/TurnstileCaptcha'
import { useBehavioralAnalysis } from '../../hooks/useBehavioralAnalysis'
import { generateDeviceFingerprint as generateEnhancedFingerprint } from '../../services/enhancedFingerprintService'
import { supabase } from '../../lib/supabase'
import { safeLocalStorage } from '../../utils/safeStorage'

const registerSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  escola: z.string().optional(),
  password: z
    .string()
    .min(8, 'A senha precisa ter pelo menos 8 caracteres')
    .regex(/[a-z]/, 'A senha precisa conter pelo menos uma letra minúscula')
    .regex(/[A-Z]/, 'A senha precisa conter pelo menos uma letra maiúscula')
    .regex(/[0-9]/, 'A senha precisa conter pelo menos um número')
    .regex(/[^A-Za-z0-9]/, 'A senha precisa conter pelo menos um símbolo (ex: !@#$%)'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"]
})

type RegisterFormData = z.infer<typeof registerSchema>

interface RegisterFormProps {
  onToggleMode: () => void
  onBackToLogin: () => void
  /**
   * Hospedado dentro de outra superfície (ex.: ConversionModal): omite o bloco
   * Logo + título e NÃO navega para /app ao autenticar — o host decide o que
   * fazer com a sessão nova.
   */
  embedded?: boolean
  /**
   * A pessoa iniciou a autenticação (submit ou Google). O ConversionModal usa
   * para guardar a intenção de download antes de o OAuth/confirmação de e-mail
   * tirar o usuário da página.
   */
  onAuthStart?: () => void
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onToggleMode, onBackToLogin, embedded = false, onAuthStart }) => {
  const { signUp, signInWithGoogle, user, loading: authLoading } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // 🛡️ SECURITY: CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaError, setCaptchaError] = useState(false)
  const captchaRef = React.useRef<TurnstileCaptchaHandle>(null)

  // 🛡️ SECURITY: Honeypot field state (to detect bots)
  const [honeypot, setHoneypot] = useState('')

  // 🤖 SECURITY: Behavioral Analysis (SEMANA 2)
  const { metrics, analyze, trackFieldInteraction } = useBehavioralAnalysis()

  // Safety net for the "Confirm email" UX: if signUp succeeds but the AuthContext
  // listener never delivers a session within 3s, Supabase is requiring confirmation.
  // Show a sticky banner so the user knows the next step instead of staring at a
  // post-toast page that never navigates.
  const [showConfirmEmailBanner, setShowConfirmEmailBanner] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const confirmTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema)
  })

  // 🔒 SECURITY: Record behavioral analysis and fingerprint after successful registration
  useEffect(() => {
    const recordSecurityData = async () => {
      if (user && !authLoading) {
        const pendingData = safeLocalStorage.getItem('pending_security_data')

        if (pendingData) {
          try {
            const { behavioral, fingerprint, timestamp } = JSON.parse(pendingData)

            // Only process if data is recent (within last 2 minutes)
            if (Date.now() - timestamp < 2 * 60 * 1000) {
              // Record behavioral analysis
              const { error: behavioralError } = await supabase.rpc('record_behavioral_analysis', {
                p_user_id: user.id,
                p_session_id: `register_${timestamp}`,
                p_metrics: behavioral.metrics,
                p_confidence_score: behavioral.confidenceScore,
                p_suspicious_patterns: behavioral.suspiciousPatterns,
                p_recommendation: behavioral.recommendation,
                p_is_likely_bot: behavioral.isLikelyBot,
                p_action: 'signup'
              })

              // Error handled silently for behavioral analysis recording

              // Record device fingerprint
              const { error: fpError } = await supabase.rpc('record_device_fingerprint', {
                p_user_id: user.id,
                p_fingerprint: fingerprint.fingerprint,
                p_components: fingerprint.components,
                p_confidence_score: fingerprint.confidence
              })

              // Error handled silently for device fingerprint recording
            }

            // Clear pending data
            safeLocalStorage.removeItem('pending_security_data')
          } catch (error) {
            safeLocalStorage.removeItem('pending_security_data')
          }
        }
      }
    }

    recordSecurityData()
  }, [user, authLoading])

  // Post-signup navigation. Trial is auto-activated by AuthContext for every new
  // authenticated user, so we only handle routing here.
  useEffect(() => {
    if (!user || authLoading) return

    // Session arrived — cancel the "Confirm email" safety-net banner.
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = null
    }
    setShowConfirmEmailBanner(false)

    // Embutido (ConversionModal): o host observa `user` e segue o fluxo dele.
    if (embedded) return

    const queryParams = new URLSearchParams(location.search)
    const redirectTo = queryParams.get('redirect_to')
    const inviteToken = queryParams.get('invite_token')

    if (redirectTo && inviteToken) {
      navigate(`${redirectTo}?token=${inviteToken}`)
    } else {
      navigate('/app')
    }
  }, [user, authLoading, navigate, location.search, embedded])

  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current)
      }
    }
  }, [])

  const handleGoogleSignIn = async () => {
    onAuthStart?.()
    try {
      setIsLoading(true)
      await signInWithGoogle()
      // The page will redirect to Google OAuth, no need for further action
    } catch (error) {
      // Error is already handled by signInWithGoogle
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: RegisterFormData) => {
    onAuthStart?.()
    // 🛡️ SECURITY CHECK 1: Honeypot field (bot detection)
    if (honeypot !== '') {
      toast.error('Erro ao criar conta. Tente novamente.')
      return
    }

    // 🛡️ SECURITY CHECK 2: CAPTCHA validation
    if (!captchaToken) {
      toast.error('Por favor, complete a verificação de segurança')
      setCaptchaError(true)
      return
    }

    setIsLoading(true)
    try {
      // 🤖 SECURITY CHECK 3: Behavioral Analysis (SEMANA 2)
      const behavioralResult = analyze()

      // Block if detected as bot
      if (behavioralResult.recommendation === 'block') {
        toast.error('Não foi possível completar o cadastro. Por favor, tente novamente mais tarde.')

        // Log blocked attempt
        await supabase.from('security_logs').insert({
          event_type: 'registration_blocked_behavioral',
          email: data.email,
          metadata: {
            confidence_score: behavioralResult.confidenceScore,
            suspicious_patterns: behavioralResult.suspiciousPatterns,
            recommendation: behavioralResult.recommendation,
            metrics: behavioralResult.metrics
          }
        })

        return
      }

      // 🔍 SECURITY CHECK 4: Enhanced Device Fingerprint (SEMANA 2)
      const deviceFp = await generateEnhancedFingerprint()

      // Proceed with signup
      await signUp(data.email, data.password, {
        nome: data.nome,
        escola: data.escola,
        captchaToken // Send CAPTCHA token for backend verification
      })

      // Arm the safety-net banner. If Supabase auto-confirms the email, the
      // onAuthStateChange listener will set `user` within ~1s and the navigation
      // effect above will clear this timer. If Dashboard has "Confirm email" ON,
      // no session is issued — and after 3s we tell the user to check their inbox.
      setSubmittedEmail(data.email)
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = setTimeout(() => {
        setShowConfirmEmailBanner(true)
      }, 3000)

      // After successful signup, record behavioral analysis and fingerprint
      // Note: user.id will be available after signup completes
      // We'll record this in a separate effect after user is created

      // Store in localStorage temporarily for post-signup recording
      safeLocalStorage.setItem('pending_security_data', JSON.stringify({
        behavioral: behavioralResult,
        fingerprint: deviceFp,
        timestamp: Date.now()
      }))

    } catch (error) {
      // Error is handled by the auth context.
      // O token CAPTCHA já foi consumido pelo edge function verify-captcha,
      // então pedimos um novo ao widget para liberar o botão de submit.
      setCaptchaToken(null)
      setCaptchaError(false)
      captchaRef.current?.reset()
    } finally {
      setIsLoading(false)
    }
  }

  // Check if registering for trial
  const queryParams = new URLSearchParams(location.search)
  const trialPlan = queryParams.get('trial')
  const planName = queryParams.get('plan')
  const isTrialRegistration = trialPlan && (trialPlan === 'premium' || trialPlan === 'escolar')

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-md mx-auto"
    >
      {!embedded && (
        <div className="text-center mb-8">
          <Logo variant="lockup-vertical" size={64} className="mb-4" />
          <h1 className="text-2xl font-bold text-ink-900 dark:text-cream-50 mb-2">Criar conta</h1>
          <p className="text-ink-500 dark:text-ink-300">
            {isTrialRegistration
              ? `Crie sua conta para iniciar o trial de ${planName || trialPlan}`
              : 'Junte-se à comunidade de educadores'
            }
          </p>
        </div>
      )}

      {/* Confirm-email safety net: shown when signUp succeeds but no session
          arrives within 3s (production "Confirm email" toggle is ON). */}
      {showConfirmEmailBanner && (
        <div
          role="status"
          className="bg-ochre-100 dark:bg-ochre-500/10 border border-ochre-300 dark:border-ochre-700 rounded-caderno-sm p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-ochre-700 dark:text-ochre-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-ink-900 dark:text-cream-50 mb-1">
                Verifique seu e-mail para ativar a conta
              </h3>
              <p className="text-sm text-ink-700 dark:text-cream-300 mb-3">
                {submittedEmail ? (
                  <>Enviamos um link de confirmação para <strong>{submittedEmail}</strong>. </>
                ) : (
                  <>Enviamos um link de confirmação para o e-mail informado. </>
                )}
                Após confirmar, você poderá entrar.
              </p>
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm font-medium text-terra-500 hover:text-terra-700 underline"
              >
                Voltar ao login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {isTrialRegistration && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-terra-50 dark:bg-terra-500/20 border border-terra-100 dark:border-terra-700 rounded-caderno-sm p-4 mb-6"
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-terra-100 dark:bg-terra-700 rounded-full flex items-center justify-center">
                <span className="text-xl">🎯</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-terra-700 dark:text-terra-100 mb-1">
                Período de Teste Gratuito por 7 Dias
              </h3>
              <p className="text-sm text-terra-700 dark:text-terra-100">
                Após criar sua conta, seu período de teste de <strong>{planName || trialPlan}</strong> será ativado automaticamente. Sem cartão de crédito necessário!
              </p>
            </div>
          </div>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Nome completo
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('nome')}
              type="text"
              id="nome"
              onFocus={() => trackFieldInteraction('nome')}
              className={`w-full pl-10 pr-4 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.nome ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="Seu nome completo"
            />
          </div>
          {errors.nome && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.nome.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('email')}
              type="email"
              id="email"
              onFocus={() => trackFieldInteraction('email')}
              className={`w-full pl-10 pr-4 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.email ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="seu@email.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="escola" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Escola (opcional)
          </label>
          <div className="relative">
            <School className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('escola')}
              type="text"
              id="escola"
              onFocus={() => trackFieldInteraction('escola')}
              className="w-full pl-10 pr-4 py-3 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50"
              placeholder="Nome da sua escola"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              onFocus={() => trackFieldInteraction('password')}
              className={`w-full pl-10 pr-12 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.password ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-400 hover:text-ink-700 dark:hover:text-cream-300"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Confirmar senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              onFocus={() => trackFieldInteraction('confirmPassword')}
              className={`w-full pl-10 pr-12 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.confirmPassword ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-400 hover:text-ink-700 dark:hover:text-cream-300"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-blood-500 dark:text-blood-500">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* 🍯 HONEYPOT: Campo invisível para detectar bots */}
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            opacity: 0,
            pointerEvents: 'none',
            tabIndex: -1
          }}
          aria-hidden="true"
        >
          <label htmlFor="website">Website (leave empty)</label>
          <input
            type="text"
            id="website"
            name="website"
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
          />
        </div>

        {/* 🛡️ CAPTCHA: Cloudflare Turnstile (invisible) */}
        <div className={captchaError ? 'border-2 border-blood-500 rounded-caderno-sm p-3 bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/10' : ''}>
          <TurnstileCaptcha
            ref={captchaRef}
            onVerify={(token) => {
              setCaptchaToken(token)
              setCaptchaError(false)
            }}
            onError={() => {
              // Não bloquear registro se CAPTCHA falhar (fallback ativo)
              setCaptchaError(false)
            }}
            onExpire={() => {
              setCaptchaToken(null)
              toast.error('Verificação de segurança expirou. Por favor, recarregue a página.')
            }}
            size="invisible"
            allowFallback={true} // Permite registro mesmo com erro de CAPTCHA
          />
          {captchaError && (
            <p className="mt-2 text-sm text-blood-500 dark:text-blood-500">
              Por favor, complete a verificação de segurança
            </p>
          )}
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="terms"
            required
            className="w-4 h-4 text-terra-500 border-cream-400 dark:border-ink-500 rounded-caderno-xs focus:ring-terra-500 bg-surface dark:bg-ink-700 mt-1"
          />
          <label htmlFor="terms" className="ml-2 text-sm text-ink-500 dark:text-ink-300">
            Eu concordo com os{' '}
            <a href="#" className="text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium">
              Termos de Uso
            </a>{' '}
            e{' '}
            <a href="#" className="text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium">
              Política de Privacidade
            </a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading || !captchaToken}
          className="w-full bg-terra-500 hover:bg-terra-700 disabled:bg-terra-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <UserPlus className="w-5 h-5" />
              <span>Criar conta</span>
            </>
          )}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-cream-400 dark:border-ink-500"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-surface dark:bg-ink-700 text-ink-400 dark:text-ink-300">
              Ou continue com
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading || authLoading}
          className="w-full bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 hover:bg-cream-50 dark:hover:bg-ink-700 disabled:opacity-50 disabled:cursor-not-allowed text-ink-900 dark:text-cream-50 py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isLoading || authLoading ? (
            <div className="w-5 h-5 border-2 border-ink-900 dark:border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Registrar com Google</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-ink-500 dark:text-ink-300">
          Já tem uma conta?{' '}
          <button
            onClick={onToggleMode}
            className="text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium"
          >
            Fazer login
          </button>
        </p>
      </div>
    </motion.div>
  )
}

export default RegisterForm